import { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  ClipboardList, 
  Stethoscope, 
  Settings,
  Menu,
  X,
  Dog,
  Clock,
  FileText,
  Activity,
  Star,
  ShoppingCart,
  TestTube,
  Package,
  Plus,
  Scissors,
  LogOut,
  User,
  Users,
  BarChart3,
  FileOutput,
  Building2,
  UserPlus,
  TrendingUp,
  ChevronDown,
  Database,
  Camera,
  Languages,
  Palmtree,
  Siren
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { cn } from '../lib/utils';
import { logout } from '../services/api';
import { toast } from 'sonner';
import { AlertBell } from '../components/AlertBell';

const navigation = [
  { name: 'Principal', href: '/', icon: LayoutDashboard },
  { name: 'Tareas', href: '/tasks', icon: ClipboardList },
  { name: 'Lab Externo', href: '/lab', icon: TestTube },
  { name: 'Casos Remitidos', href: '/referrals', icon: Building2 },
  { name: 'Compras', href: '/compras', icon: ShoppingCart },
  { name: 'Reseñas', href: '/reviews', icon: Star },
  { name: 'Surgical Logbook', href: '/surgical-logbook', icon: Scissors },
  { name: 'Auxiliares', href: '/auxiliares', icon: Users },
  { name: 'Vacaciones', href: '/vacaciones', icon: Palmtree },
  { name: 'Urgencias', href: '/urgencias', icon: Siren },
  { name: 'Control Horario', href: '/fichajes', icon: Clock },
  { name: 'Informes', href: '/informes', icon: FileText },
  { name: 'M-m', href: '/mm', icon: Languages },
  { name: 'Configuración', href: '/settings', icon: Settings },
];

// Full navigation (hidden - preserved for future use)
const fullNavigation = [
  { name: 'Principal', href: '/', icon: LayoutDashboard },
  { name: 'Tareas', href: '/tasks', icon: ClipboardList },
  { name: 'Visita', href: '/ai-report', icon: Stethoscope },
  { name: 'Revisión', href: '/revision-report', icon: TrendingUp },
  { name: 'Historial', href: '/history', icon: FileText },
  { name: 'Hospitalizados', href: '/hospitalized', icon: Dog },
  { 
    name: 'Anestesia y Cirugía', 
    href: '/anesthesia', 
    icon: Activity,
  },
  { 
    name: 'Anestesia y TAC', 
    href: '/anesthesia-tac', 
    icon: Camera,
  },
  { 
    name: 'Tratamiento / Alta', 
    href: '/treatment', 
    icon: FileOutput,
    submenu: [
      { name: 'Generar Informe', href: '/treatment/generate' },
      { name: 'Medicamentos (BD)', href: '/treatment/medications' },
      { name: 'Presets', href: '/treatment/presets' },
    ]
  },
  { name: 'Casos Remitidos', href: '/referrals', icon: Building2 },
  { name: 'Lab Externo', href: '/lab', icon: TestTube },
  { name: 'Pedidos', href: '/orders', icon: ShoppingCart },
  { 
    name: 'Autorizaciones', 
    href: '/authorizations', 
    icon: FileText,
    submenu: [
      { name: 'Autorizaciones', href: '/authorizations' },
      { name: 'Consentimiento Informado', href: '/informed-consent' },
    ]
  },
  { name: 'Intérprete', href: '/interpreter', icon: Languages },
  { name: 'Facturas', href: '/invoices', icon: FileText },
  { name: 'Turnos', href: '/shifts', icon: Clock },
  { name: 'Urgencias', href: '/commissions', icon: Activity },
  { name: 'Vacaciones', href: '/vacations', icon: Clock },
  { name: 'Sugerencias', href: '/suggestions', icon: Star },
  { name: 'Reseñas', href: '/reviews', icon: Star },
  { name: 'Reportes', href: '/reports', icon: BarChart3 },
  { name: 'Configuración', href: '/settings', icon: Settings },
];

export const Layout = ({ children, user, onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const location = useLocation();
  const navigate = useNavigate();

  // Auto-expand treatment menu if on treatment page
  useEffect(() => {
    if (location.pathname.startsWith('/treatment') || location.pathname === '/discharge-instructions') {
      setExpandedMenus(prev => ({ ...prev, 'Tratamiento / Alta': true }));
    }
    if (location.pathname.startsWith('/patients') || location.pathname === '/new-patient') {
      setExpandedMenus(prev => ({ ...prev, 'Pacientes': true }));
    }
  }, [location.pathname]);

  return (
    <div className="main-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="sidebar-overlay lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn("sidebar", sidebarOpen && "open")}>
        <div className="flex flex-col h-full bg-gradient-to-b from-white to-[#F5F7FA]">
          {/* Logo */}
          <div className="p-5 border-b border-[#E6F7F4]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img 
                  src="/logo.png" 
                  alt="Benivet" 
                  className="w-12 h-12 object-contain"
                />
                <div>
                  <h1 className="font-bold text-xl gradient-text">Benivet</h1>
                  <p className="text-xs text-[#8A94A3]">Ops Manager</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                className="lg:hidden"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Medication Alert - Hidden in simplified version */}
          {/* {pendingMeds.length > 0 && (
            <div className="mx-4 mt-4 medication-alert">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Pill className="w-4 h-4 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-800">
                    {pendingMeds.length} medicación{pendingMeds.length > 1 ? 'es' : ''} pendiente{pendingMeds.length > 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-amber-600">Requiere atención</p>
                </div>
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => setShowMedsDetail(!showMedsDetail)}
                  className="text-amber-700 hover:bg-amber-100"
                >
                  {showMedsDetail ? 'Ocultar' : 'Ver'}
                </Button>
              </div>
              {showMedsDetail && (
                <div className="mt-2 space-y-2 max-h-60 overflow-y-auto">
                  {pendingMeds.map((med, idx) => (
                    <div key={`el-${idx}`} className="p-2 bg-amber-50 rounded border border-amber-200 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-amber-900">{med.patient_name || 'Paciente'}</span>
                        <span className="text-amber-600">{med.medication_name || med.drug_name}</span>
                      </div>
                      <div className="text-amber-700 mt-1">
                        {med.dose} · {med.scheduled_time ? new Date(med.scheduled_time).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : 'Sin hora'}
                      </div>
                    </div>
                  ))}
                  <Button 
                    size="sm" 
                    className="w-full mt-2 bg-amber-600 hover:bg-amber-700"
                    onClick={() => { navigate('/hospitalized?showMedications=true'); setShowMedsDetail(false); }}
                  >
                    Ir a Hospitalizados
                  </Button>
                </div>
              )}
            </div>
          )} */}

          {/* Sound Alerts Toggle - Hidden in simplified version */}
          {/* <div className="mx-4 mt-2">
            <button
              onClick={() => setSoundAlertsEnabled(!soundAlertsEnabled)}
              className={`w-full flex items-center gap-3 text-sm p-2 rounded-lg transition-all ${
                soundAlertsEnabled 
                  ? 'text-[#004F5D] bg-[#E6F7F4] hover:bg-[#D0F0EA]' 
                  : 'text-slate-500 bg-slate-100 hover:bg-slate-200'
              }`}
              data-testid="toggle-sound-alerts"
            >
              {soundAlertsEnabled ? (
                <>
                  <Volume2 className="w-4 h-4" />
                  <span className="text-xs">Alertas sonoras activas</span>
                </>
              ) : (
                <>
                  <VolumeX className="w-4 h-4" />
                  <span className="text-xs">Alertas sonoras desactivadas</span>
                </>
              )}
            </button>
          </div> */}

          {/* Nuevo Paciente - Menú Expandible - Hidden in simplified version */}
          {/* <div className="px-4 pt-4">
            <div className="bg-gradient-to-r from-[#00AE95] to-[#008577] rounded-xl shadow-lg overflow-hidden">
              <button
                onClick={() => setExpandedMenus(prev => ({ ...prev, 'Pacientes': !prev['Pacientes'] }))}
                className="flex items-center gap-3 w-full p-3 text-white hover:bg-white/10 transition-all duration-200"
                data-testid="nav-pacientes-menu-btn"
              >
                <div className="p-2 bg-white/20 rounded-lg">
                  <UserPlus className="w-5 h-5" />
                </div>
                <div className="flex-1 text-left">
                  <span className="font-semibold">Pacientes</span>
                  <p className="text-xs text-white/80">Gestión de pacientes</p>
                </div>
                <ChevronDown className={cn(
                  "w-4 h-4 transition-transform",
                  expandedMenus['Pacientes'] && "rotate-180"
                )} />
              </button>
              {expandedMenus['Pacientes'] && (
                <div className="px-3 pb-3 space-y-1">
                  <NavLink
                    to="/patients/register"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => cn(
                      "flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors",
                      isActive 
                        ? "bg-white/30 text-white font-medium" 
                        : "text-white/90 hover:bg-white/20"
                    )}
                    data-testid="nav-registro-paciente"
                  >
                    <Plus className="w-4 h-4" />
                    Registro Nuevo Paciente
                  </NavLink>
                  <NavLink
                    to="/patients/database"
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => cn(
                      "flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors",
                      isActive 
                        ? "bg-white/30 text-white font-medium" 
                        : "text-white/90 hover:bg-white/20"
                    )}
                    data-testid="nav-bd-pacientes"
                  >
                    <Database className="w-4 h-4" />
                    BD Pacientes
                  </NavLink>
                </div>
              )}
            </div>
          </div> */}

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1.5 overflow-y-auto">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href || 
                (item.href !== '/' && location.pathname.startsWith(item.href));
              const hasSubmenu = item.submenu && item.submenu.length > 0;
              const isExpanded = expandedMenus[item.name];
              
              if (hasSubmenu) {
                return (
                  <div key={item.name}>
                    <button
                      onClick={() => setExpandedMenus(prev => ({ ...prev, [item.name]: !prev[item.name] }))}
                      className={cn(
                        "sidebar-link w-full justify-between",
                        isActive && "sidebar-link-active"
                      )}
                      data-testid={`nav-${item.name.toLowerCase().replace(/[\s/]/g, '-')}`}
                    >
                      <div className="flex items-center gap-3">
                        <item.icon className={cn("w-5 h-5", isActive && "text-[#00AE95]")} />
                        <span>{item.name}</span>
                      </div>
                      <ChevronDown className={cn(
                        "w-4 h-4 transition-transform",
                        isExpanded && "rotate-180"
                      )} />
                    </button>
                    {isExpanded && (
                      <div className="ml-8 mt-1 space-y-1">
                        {item.submenu.map((subitem) => {
                          const isSubActive = location.pathname === subitem.href;
                          return (
                            <NavLink
                              key={subitem.href}
                              to={subitem.href}
                              onClick={() => setSidebarOpen(false)}
                              className={cn(
                                "block px-3 py-2 text-sm rounded-lg transition-colors",
                                isSubActive 
                                  ? "bg-[#E6F7F4] text-[#00AE95] font-medium" 
                                  : "text-[#8A94A3] hover:bg-slate-100"
                              )}
                              data-testid={`nav-sub-${subitem.name.toLowerCase().replace(/[\s()]/g, '-')}`}
                            >
                              {subitem.name}
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    "sidebar-link",
                    isActive && "sidebar-link-active"
                  )}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className={cn("w-5 h-5", isActive && "text-[#00AE95]")} />
                  <span>{item.name}</span>
                  {item.href === '/hospitalized' && pendingMeds.length > 0 && (
                    <Badge className="ml-auto bg-amber-500 text-white text-xs">
                      {pendingMeds.length}
                    </Badge>
                  )}
                </NavLink>
              );
            })}
          </nav>

          {/* Voice Assistant Hint - Hidden in simplified version */}
          {/* <div className="p-4 border-t border-[#E6F7F4]">
            <button 
              onClick={handleVoiceCommand}
              className="w-full flex items-center gap-3 text-sm text-[#004F5D] bg-gradient-to-r from-[#E6F7F4] to-[#D0F0EA] p-3 rounded-xl hover:shadow-md transition-all"
            >
              <div className="p-2 bg-[#00AE95] rounded-lg">
                <Mic className="w-4 h-4 text-white" />
              </div>
              <div className="text-left">
                <p className="font-medium">Asistente de Voz</p>
                <p className="text-xs text-[#8A94A3]">Di un comando...</p>
              </div>
            </button>
          </div> */}

          {/* User Info & Logout */}
          {user && (
            <div className="p-4 border-t border-[#E6F7F4]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00AE95] to-[#004F5D] flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#004F5D]">{user.full_name}</p>
                    <p className="text-xs text-[#8A94A3]">{user.username}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <AlertBell />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      logout();
                      onLogout?.();
                    }}
                    className="text-slate-500 hover:text-red-500 hover:bg-red-50"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="p-4 border-t border-[#E6F7F4]">
            <p className="text-xs text-[#8A94A3] text-center">
              © Benivet Clínica Veterinaria - Benissa
            </p>
            <p className="text-[10px] text-[#B0B8C4] text-center mt-1">
              Todos los derechos de autor reservados. Propiedad intelectual. Constantino Llobell Durá.
            </p>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="main-content">
        {/* Mobile header */}
        <header className="lg:hidden sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-[#E6F7F4] px-4 py-3">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5 text-[#004F5D]" />
            </Button>
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="Benivet" className="w-8 h-8 object-contain" />
              <span className="font-bold gradient-text">Benivet Ops</span>
            </div>
            <AlertBell />
          </div>
        </header>

        {/* Page content */}
        <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto animate-fade-in">
          {children}
        </div>
      </main>

      {/* Global Voice Assistant Button (Desktop) - Hidden in simplified version */}
      {/* <button
        onClick={handleVoiceCommand}
        className={cn(
          "voice-assistant-btn hidden lg:flex",
          isListening && "listening"
        )}
        data-testid="voice-assistant-btn"
      >
        <Mic className="w-7 h-7 text-white" />
      </button> */}

      {/* Feedback Widget - Hidden in simplified version */}
      {/* <FeedbackWidget user={user} /> */}

      {/* Voice Modal - Hidden in simplified version */}
      {/* {showVoiceModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 text-center animate-scale-in">
            <div className={cn(
              "w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-6",
              isListening ? "bg-[#00AE95] animate-pulse" : "bg-[#004F5D]"
            )}>
              {isProcessing ? (
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-white border-t-transparent" />
              ) : (
                <Mic className="w-12 h-12 text-white" />
              )}
            </div>
            <h3 className="text-xl font-bold text-[#004F5D] mb-2">
              {isListening ? 'Escuchando...' : isProcessing ? 'Procesando...' : 'Comando recibido'}
            </h3>
            {voiceTranscript && (
              <p className="text-[#8A94A3] italic">&ldquo;{voiceTranscript}&rdquo;</p>
            )}
            <p className="text-sm text-[#8A94A3] mt-4">
              {isListening ? 'Habla ahora para dar un comando' : 'Un momento...'}
            </p>
            <Button 
              variant="ghost" 
              className="mt-4"
              onClick={() => setShowVoiceModal(false)}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )} */}
    </div>
  );
};
